import { useCallback } from "react";
import { useSnackbar } from "hooks/useSnackbar";
import { SnackbarType } from "components/Snackbar/types";
import { MAX_UPLOAD_LIMIT_BYTES } from "utils/constants";
import { optimizeImageForUpload, type ImageOptimizationStatus } from "utils/uploadImageOptimizer";
import i18n from "i18n";

export interface PreparedUploadBatch {
  // Same order and length as the input. A file that was not optimized is the SAME object.
  files: File[];
  statuses: ImageOptimizationStatus[];
  // The ORIGINAL files that came back optimized — what `announceOptimized` takes.
  optimizedSources: File[];
}

interface PrepareOptions {
  maxBytes?: number;
  isSizeExempt?: (file: File) => boolean;
  // Skip the notice — for a caller that announces later, once it knows which files it kept.
  silent?: boolean;
}

// Sources the user was already told about. The same original can reach preparation more than once
// (UploadBox → useGoogleStorage, a retried drop, a cached canvas validation); the message must not
// repeat. A source is marked only when a notice actually named it — a silent pass marks nothing.
const announced = new WeakSet<File>();

export const useUploadImagePreparation = () => {
  const { showSnackbar } = useSnackbar();

  // ONE info notice for the given optimized originals, counting only those not announced before.
  const announceOptimized = useCallback(
    (sources: File[]) => {
      // Deduplicated by identity first: the same original listed twice is still one image.
      const fresh = Array.from(new Set(sources)).filter((source) => !announced.has(source));
      if (fresh.length === 0) return;
      fresh.forEach((source) => announced.add(source));
      showSnackbar(i18n.t("validation:file.optimized", { count: fresh.length }), SnackbarType.Info);
    },
    [showSnackbar]
  );

  // Shrinks every eligible image over `maxBytes` (see `utils/uploadImageOptimizer`) and, unless
  // `silent`, announces the batch once. Files at or under the cap, exempt files and non-images pass
  // through.
  const prepareFiles = useCallback(
    async (files: File[], { maxBytes = MAX_UPLOAD_LIMIT_BYTES, isSizeExempt, silent = false }: PrepareOptions = {}): Promise<PreparedUploadBatch> => {
      const results = await Promise.all(files.map((file) => (isSizeExempt?.(file) ? Promise.resolve({ file, status: "untouched" as const }) : optimizeImageForUpload(file, maxBytes))));
      const optimizedSources = files.filter((_, index) => results[index].status === "optimized");

      if (!silent) announceOptimized(optimizedSources);

      return {
        files: results.map((result) => result.file),
        statuses: results.map((result) => result.status),
        optimizedSources,
      };
    },
    [announceOptimized]
  );

  return { prepareFiles, announceOptimized };
};
